import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle, XCircle, Loader2, Terminal, Send } from 'lucide-react';
import { useUiStore } from '@/stores/ui';
import { toast } from 'sonner'; 
import { http } from '@/lib/http';
import useSyncStore from '@/stores/sync';
import { useConsoleStore } from '@/stores/console';
import { Terminal as XTerm } from 'xterm';
import { FitAddon } from 'xterm-addon-fit';
import 'xterm/css/xterm.css';
import { Input } from '@/components/ui/input';
import { useProposalsStore } from '@/stores/proposals';
interface FinalPayload {
  status: 'success' | 'error';
  taskId?: string;
  proposalId?: string;
  message?: string;
}

const SCROLL_THRESHOLD = 5; // px tolerance to treat the view as "at bottom"

const InteractiveTerminal = ({ proposalId }: { proposalId: string }) => {
    const navigate = useNavigate();
    const [sessionName, setSessionName] = useState<string | null>(null);
    const [paneId, setPaneId] = useState<string | null>(null);
    const { status, connect, disconnect, sendPaneInput, resizePane } = useConsoleStore((state) => ({
        status: state.status,
        connect: state.connect,
        disconnect: state.disconnect,
        sendPaneInput: state.sendPaneInput,
        resizePane: state.resizePane,
    }));
    const terminalRef = useRef<{ terminal: XTerm; fitAddon: FitAddon } | null>(null);
    const [isFinalizing, setIsFinalizing] = useState(false);
    const lastContentRef = useRef<string>('');
    const { finalizeInteractiveSession } = useProposalsStore((state) => ({
        finalizeInteractiveSession: state.finalizeInteractiveSession,
    }));

    useEffect(() => {
        const startSession = async () => {
            try {
                const { sessionName, paneId, prompt } = await http<{ sessionName: string, paneId: string, prompt: string }>(`/api/proposals/${proposalId}/start-interactive-session`, {
                    method: 'POST',
                });
                setSessionName(sessionName);
                setPaneId(paneId);
                connect(sessionName); // Connects to the console SSE stream for this session
            } catch (error: any) {
                toast.error("Failed to start interactive session.", { description: error.message });
            }
        };
        startSession();

        return () => {
            disconnect();
        };
    }, [proposalId, connect, disconnect]);

    const activePane = useMemo(() => {
        if (!sessionName || !status?.panes) return null;
        // The session might have multiple panes if the config is weird, but we only care about the one we started.
        return status.panes.find(p => p.id === paneId);
    }, [status, sessionName, paneId]);

    // Initialize terminal
    useEffect(() => {
        if (activePane && !terminalRef.current) {
            const terminal = new XTerm({ convertEol: true, scrollback: 10000 });
            const fitAddon = new FitAddon();
            terminal.loadAddon(fitAddon);

            terminal.onData((data) => {
                if (paneId) sendPaneInput(paneId, data);
            });
            
            terminal.onResize(({ cols, rows }) => {
                if (paneId && cols > 0 && rows > 0) {
                    resizePane(paneId, rows, cols);
                }
            });

            terminalRef.current = { terminal, fitAddon };
        }
    }, [activePane, paneId, sendPaneInput, resizePane]);

    // Write content to terminal
    useEffect(() => {
        if (terminalRef.current && activePane?.content != null) {
            const term = terminalRef.current.terminal;
            const lastContent = lastContentRef.current;
            const newContent = activePane.content;

            if (newContent !== lastContent) {
                if (newContent.startsWith(lastContent)) {
                    const diff = newContent.substring(lastContent.length);
                    term.write(diff);
                } else {
                    term.clear();
                    term.write(newContent);
                }
                lastContentRef.current = newContent;
            }
        }
    }, [activePane?.content]);

    const handleFinalize = async () => {
        setIsFinalizing(true);
        const toastId = toast.loading("Finalizing interactive session...");
        try {
            const result = await finalizeInteractiveSession(proposalId);
            toast.success("Proposal finalized from session.", { id: toastId });
            navigate(`/pages/review/?taskId=${result.taskId}&proposalId=${result.proposalId}`);
        } catch (error: any) {
            toast.error("Failed to finalize session.", { id: toastId, description: error.message });
        } finally {
            setIsFinalizing(false);
        }
    };

    return (
        <div className="flex-1 flex flex-col w-full h-full overflow-hidden">
             <CardHeader className="p-2 sm:p-4 md:p-6">
                <CardTitle>Interactive Generation</CardTitle>
                <CardDescription className="font-mono text-xs">
                    Proposal ID: {proposalId} | Session: {sessionName}
                </CardDescription>
            </CardHeader>
            <CardContent className="flex-1 overflow-hidden p-0">
                <div
                    ref={(el) => {
                        if (el && terminalRef.current && !terminalRef.current.terminal.element) {
                            const { terminal, fitAddon } = terminalRef.current;
                            terminal.open(el);
                            fitAddon.fit();
                            if (activePane?.content && lastContentRef.current !== activePane.content) {
                                terminal.write(activePane.content);
                                lastContentRef.current = activePane.content;
                            }
                        }
                    }}
                    className="h-full w-full bg-black"
                />
            </CardContent>
            <CardFooter className="p-2 sm:p-4 md:p-6">
                <Button onClick={handleFinalize} disabled={isFinalizing}>
                    {isFinalizing ? <Loader2 className="mr-2 h-4 w-4 animate-spin"/> : <CheckCircle className="mr-2 h-4 w-4" />}
                    Finalize &amp; Create Proposal
                </Button>
            </CardFooter>
        </div>
    );
};

export default function GenerationProgressPage() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const taskId = searchParams.get('taskId');
    const proposalId = searchParams.get('proposalId');
    const isInteractive = searchParams.get('interactive') === 'true';
    const { setHeaderTitle } = useUiStore();
    
    const [output, setOutput] = useState<string>('');
    const [isComplete, setIsComplete] = useState(false);
    const [isSyncing, setIsSyncing] = useState(false);
    const [result, setResult] = useState<FinalPayload | null>(null);
    const [isAtBottom, setIsAtBottom] = useState(true);
    const [progressHash, setProgressHash] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const outputRef = useRef<HTMLDivElement>(null);
    const shouldAutoScrollRef = useRef(true);
    const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const { setReviewTaskId } = useUiStore();

    useEffect(() => {
        if (!taskId) return;
        setHeaderTitle(`Generating proposal...`);
    }, [setHeaderTitle, taskId]);

    const scrollToBottom = () => {
        if (outputRef.current) {
            outputRef.current.scrollTop = outputRef.current.scrollHeight;
        }
    };
    useEffect(() => {
        if (isInteractive) {
        // Interactive mode uses a different streaming mechanism (console SSE)
        return;
        }

        if (!taskId) {
            setError("No Task ID provided in the URL.");
            setIsComplete(true);
            return;
        }
        if (!proposalId) {
            setError("No Proposal ID provided in the URL.");
            setIsComplete(true);
            return;
        }

        const handleScroll = () => {
            if (outputRef.current) {
                const { scrollTop, scrollHeight, clientHeight } = outputRef.current;
                const isBottom = scrollTop + clientHeight >= scrollHeight - SCROLL_THRESHOLD;
                setIsAtBottom(isBottom);
                shouldAutoScrollRef.current = isBottom;
            }
        };
        const outputElement = outputRef.current;
        if (outputElement) {
            outputElement.addEventListener('scroll', handleScroll);
        }
        const pollProgress = async () => {
            try {
                let endpoint = `/api/proposals/${proposalId}/progress`;
                if (progressHash) {
                    endpoint += `?last_hash=${encodeURIComponent(progressHash)}`;
                }
                const data = await http<{ status: string, output: string, result: FinalPayload | null, hash: string }>(endpoint);

                // A 204 response will result in 'undefined' from our http helper.
                if (data === undefined) {
                    return; // No change, do nothing.
                }

                setOutput(data.output);
                setProgressHash(data.hash); // Store the new hash for the next poll.
                if (data.status === 'complete' || data.status === 'failed') {
                    setIsComplete(true);
                    setResult(data.result);
                    if (data.result?.status === 'error') {
                        setError(data.result.message || `The generation process failed.`);
                    }
                    if (intervalRef.current) {
                        clearInterval(intervalRef.current);
                    }
                }
            } catch (e: any) {
                        setError(`Connection to the server was lost: ${e.message}`);
                console.error('Polling failed:', e);
                setIsComplete(true);
                if (intervalRef.current) {
                    clearInterval(intervalRef.current);
                }
            }
        };
       
        // Initial scroll to bottom
        setTimeout(() => {
            const container = outputRef.current;
            if (!container) {
                return;
            }

            if (shouldAutoScrollRef.current) {
                scrollToBottom();
                setIsAtBottom(true);
                return;
            }

            const { scrollTop, scrollHeight, clientHeight } = container;
            const isBottom = scrollTop + clientHeight >= scrollHeight - SCROLL_THRESHOLD;
            setIsAtBottom(isBottom);
        }, 0);
        pollProgress(); // Initial poll
        intervalRef.current = setInterval(pollProgress, 750);
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
            outputRef.current?.removeEventListener('scroll', handleScroll);
        };
    }, [taskId, proposalId, error, progressHash, isInteractive]);

    // Auto-scroll when new output is added
    useEffect(() => {
        if (shouldAutoScrollRef.current) {
            scrollToBottom();
        }
    }, [output]);
    const handleCancel = async () => {
        if (!proposalId) return;
        try {
            await http(`/api/proposals/${proposalId}/cancel`, {
                method: 'POST',
            });
            toast.info("Generation has been cancelled.");
            // The polling should eventually pick up the 'failed' or a new 'cancelled' state.
        } catch (e: any) {
            toast.error("Failed to cancel generation.", { description: e.message });
        }
    };
    useEffect(() => {
        if (isComplete && result) {
            if (result.status === 'success') {
                setIsSyncing(true);
                toast.info(`Generation complete. Syncing latest state...`);
                useSyncStore.getState().fetchSnapshot()
                    .then(() => {
                        toast.success("Sync complete. Ready for review.");
                    })
                    .catch((err) => {
                        toast.error("Failed to sync state after generation.", { description: err.message });
                    })
                    .finally(() => {
                        setIsSyncing(false);
                    });
            } else if (result.status === 'error') {
                setError(result.message || `The generation process failed.`);
            }
        }
    }, [isComplete, result]);
    const handleNavigation = () => {
        if (result?.status === 'success' && result.taskId) {
            setReviewTaskId(result.taskId);
            const reviewUrl = proposalId ? `/pages/review/?taskId=${result.taskId}&proposalId=${proposalId}` : `/pages/review/?taskId=${result.taskId}`;
            navigate(reviewUrl);
        } else {
            navigate('/pages/workspace/');
        }
    };

    if (isInteractive && proposalId) {
      return (
          <div className="p-0 sm:p-2 md:p-4 h-full flex flex-col">
              <Card className="flex-1 flex flex-col w-full h-full rounded-none border-0 sm:rounded-xl sm:border shadow-none sm:shadow"><InteractiveTerminal proposalId={proposalId} /></Card></div>
      );
    }
    return (
        <div className="p-0 sm:p-2 md:p-4 h-full flex flex-col">
            <Card className="flex-1 flex flex-col w-full h-full rounded-none border-0 sm:rounded-xl sm:border shadow-none sm:shadow">
                <CardHeader className="p-2 sm:p-4 md:p-6">
                    <CardTitle>Live Generation Log</CardTitle>
                    <CardDescription className="font-mono text-xs">
                        Proposal ID: {proposalId}
                    </CardDescription>
                </CardHeader>
                <CardContent className="flex-1 overflow-hidden p-0">
                    <div ref={outputRef} className="h-full bg-black/80 text-white rounded-none p-2 sm:p-4 overflow-y-auto font-mono">
                        <pre className="text-sm whitespace-pre-wrap">{output}</pre>
                    </div>
                </CardContent>
                <CardFooter className="flex justify-end p-2 sm:p-4 md:p-6">
                    {isComplete ? (
                        result?.status === 'success' ? (
                            <Button 
                                onClick={handleNavigation} 
                                disabled={isSyncing}
                            >
                                {isSyncing ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Syncing...</> : <><CheckCircle className="mr-2 h-4 w-4" /> Go to Review</>}
                            </Button>
                        ) : (
                            <div className="w-full text-left space-y-2">
                                <p className="text-destructive font-semibold flex items-center text-sm">
                                    <XCircle className="mr-2 h-4 w-4" />
                                    {error || result?.message || "An unknown error occurred."}
                                </p>
                                <Button variant="outline" onClick={handleNavigation}>
                                    Back to Workspace
                                </Button>
                            </div>
                        )
                    ) : (
                        <div className="flex w-full justify-between items-center">
                            <div className="flex items-center text-muted-foreground font-mono text-sm">
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Generating...
                            </div>
                            <Button variant="destructive" onClick={handleCancel}>
                                Cancel
                            </Button>
                        </div>
                    )}
                </CardFooter>
            </Card>
        </div>
    );
}